/**
 * NVIDIA Spectrum Ethernet Switch Database
 *
 * Complete specifications for NVIDIA Spectrum Ethernet switches including
 * SN5600 (800G), SN4600 (200G/100G), SN3800 (100G), and SN2201 (OOB).
 *
 * Data sources: research/switch-specs.md
 */

import type { SwitchModel } from '../../types/switches';

/**
 * Spectrum SN5600 (Spectrum-4, 800GbE)
 */
export const SPECTRUM_SN5600: SwitchModel = {
  id: 'nvidia-sn5600',
  name: 'SN5600',
  vendor: 'NVIDIA',
  productName: 'Spectrum SN5600 (Spectrum-4)',
  fabricType: 'Ethernet',
  typicalRoles: ['leaf', 'spine'],
  ports: {
    totalPorts: 64,
    speed: {
      value: 800,
      unit: 'Gbps',
      displayName: '800GbE',
    },
    portType: 'OSFP',
    supportsBreakout: true,
    breakoutOptions: [
      '64x800G',
      '128x400G',
      '256x200G',
      '256x100G',
      'Mixed 1/10/25/50/100/200/400/800GbE',
    ],
  },
  power: {
    typicalWatts: 940, // With passive cables
    maxWatts: 1500, // With active optics (estimated)
    idleWatts: 400,
    perPortWatts: 12,
    psuConfig: {
      count: 2,
      capacity: 2000,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 2,
    widthMm: 428,
    depthMm: 566,
    weightKg: 20,
    airflow: 'front-to-back',
    maxBtuPerHour: 5120,
  },
  performance: {
    switchingCapacityTbps: 51.2,
    forwardingRateMpps: 33300,
    latencyNs: 300, // Ethernet typical
    bufferSizeMB: 160,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: true,
    evpn: true,
    rdma: true, // RoCEv2 support
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    sonic: true,
    additionalFeatures: [
      'NVIDIA Spectrum-4 ASIC',
      'Spectrum-X platform compatible',
      'SONiC fully supported',
      'Cumulus Linux support',
      'Hexa-core x86 processor',
      '32GB RAM, 160GB SSD',
      'Secure Boot',
    ],
  },
  metadata: {
    productUrl: 'https://www.delltechnologies.com/asset/en-us/products/networking/technical-support/nvidia-spectrum-sn5600-datasheet.pdf',
    releaseYear: 2023,
    endOfLife: false,
    notes: 'Latest 800G Ethernet switch for AI workloads. Part of NVIDIA Spectrum-X platform.',
  },
};

/**
 * Spectrum SN4600 (Spectrum-3, 200GbE)
 */
export const SPECTRUM_SN4600: SwitchModel = {
  id: 'nvidia-sn4600',
  name: 'SN4600',
  vendor: 'NVIDIA',
  productName: 'Spectrum SN4600 (Spectrum-3)',
  fabricType: 'Ethernet',
  typicalRoles: ['leaf', 'spine'],
  ports: {
    totalPorts: 64,
    speed: {
      value: 200,
      unit: 'Gbps',
      displayName: '200GbE',
    },
    portType: 'QSFP56',
    supportsBreakout: true,
    breakoutOptions: ['64x200G', '128x100G', '256x50G', 'Mixed 1/10/25/50/100/200GbE'],
  },
  power: {
    typicalWatts: 600, // With passive cables (ATIS standard)
    maxWatts: 1000, // With active optics (estimated)
    idleWatts: 280,
    perPortWatts: 8,
    psuConfig: {
      count: 2,
      capacity: 1200,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 2,
    widthMm: 428,
    depthMm: 566,
    weightKg: 14.64,
    airflow: 'front-to-back',
    maxBtuPerHour: 3410,
  },
  performance: {
    switchingCapacityTbps: 12.8,
    forwardingRateMpps: 8330,
    latencyNs: 350,
    bufferSizeMB: 64,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: true,
    evpn: true,
    rdma: true,
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    sonic: true,
    additionalFeatures: [
      'NVIDIA Spectrum-3 ASIC',
      'Intel x86 2.20GHz Quad Core',
      '16GB RAM, 60GB SSD',
      'PCIe 4x Gen 3.0',
    ],
  },
  metadata: {
    productUrl: 'https://docs.nvidia.com/networking/display/sn4000/specifications',
    releaseYear: 2021,
    endOfLife: false,
    notes: '200G Ethernet for AI/Cloud workloads.',
  },
};

/**
 * Spectrum SN4600C (Spectrum-3, 100GbE)
 */
export const SPECTRUM_SN4600C: SwitchModel = {
  id: 'nvidia-sn4600c',
  name: 'SN4600C',
  vendor: 'NVIDIA',
  productName: 'Spectrum SN4600C (Spectrum-3)',
  fabricType: 'Ethernet',
  typicalRoles: ['leaf', 'spine'],
  ports: {
    totalPorts: 64,
    speed: {
      value: 100,
      unit: 'Gbps',
      displayName: '100GbE',
    },
    portType: 'QSFP28',
    supportsBreakout: true,
    breakoutOptions: ['64x100G', '128x50G', 'Mixed 1/10/25/40/50/100GbE'],
  },
  power: {
    typicalWatts: 466, // With passive cables
    maxWatts: 800, // With active optics
    idleWatts: 220,
    perPortWatts: 6,
    psuConfig: {
      count: 2,
      capacity: 1000,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 2,
    widthMm: 428,
    depthMm: 566,
    weightKg: 13,
    airflow: 'front-to-back',
    maxBtuPerHour: 2730,
  },
  performance: {
    switchingCapacityTbps: 6.4,
    forwardingRateMpps: 4165,
    latencyNs: 350,
    bufferSizeMB: 32,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: true,
    evpn: true,
    rdma: true,
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    sonic: true,
    additionalFeatures: [
      'NVIDIA Spectrum-3 ASIC',
      'Intel x86 Quad Core',
      '8GB RAM, 30GB SSD',
    ],
  },
  metadata: {
    productUrl: 'https://docs.nvidia.com/networking/display/sn4000/specifications',
    releaseYear: 2021,
    endOfLife: false,
    notes: '100G variant with lower power consumption and smaller storage than SN4600.',
  },
};

/**
 * Spectrum SN3800 (Spectrum-2, 100GbE)
 */
export const SPECTRUM_SN3800: SwitchModel = {
  id: 'nvidia-sn3800',
  name: 'SN3800',
  vendor: 'NVIDIA',
  productName: 'Spectrum SN3800 (Spectrum-2)',
  fabricType: 'Ethernet',
  typicalRoles: ['spine', 'super-spine'],
  ports: {
    totalPorts: 64,
    speed: {
      value: 100,
      unit: 'Gbps',
      displayName: '100GbE',
    },
    portType: 'QSFP28',
    supportsBreakout: true,
    breakoutOptions: ['64x100G', 'Mixed 10/25/40/50/100GbE'],
  },
  power: {
    typicalWatts: 700, // Estimated based on 1100W PSUs
    maxWatts: 1100, // Based on PSU capacity
    idleWatts: 300,
    perPortWatts: 8,
    psuConfig: {
      count: 2,
      capacity: 1100,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 2,
    widthMm: 428,
    depthMm: 566,
    weightKg: 16,
    airflow: 'front-to-back',
    maxBtuPerHour: 3750,
  },
  performance: {
    switchingCapacityTbps: 12.8,
    forwardingRateMpps: 8330,
    latencyNs: 400,
    bufferSizeMB: 16,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: true,
    evpn: true,
    rdma: true,
    adaptiveRouting: false,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    sonic: true,
    additionalFeatures: [
      'NVIDIA Spectrum-2 ASIC',
      'Powerful x86 processor',
      '16MB shared buffer',
    ],
  },
  metadata: {
    productUrl: 'https://www.etb-tech.com/mellanox-sn3800-switch-64-x-100gb-qsfp28-ports-sw02365.html',
    releaseYear: 2019,
    endOfLife: false,
    notes: 'Spine/super-spine switch for large virtualized data centers. Power specs estimated from PSU capacity.',
  },
};

/**
 * Spectrum SN2201 (Spectrum, 1GbE OOB Management)
 */
export const SPECTRUM_SN2201: SwitchModel = {
  id: 'nvidia-sn2201',
  name: 'SN2201',
  vendor: 'NVIDIA',
  productName: 'Spectrum SN2201',
  fabricType: 'Ethernet',
  typicalRoles: ['management'],
  ports: {
    totalPorts: 52, // 48x1GbE + 4x100GbE
    speed: {
      value: 1,
      unit: 'Gbps',
      displayName: '1GbE',
    },
    portType: 'SFP+',
    supportsBreakout: false,
    dedicatedUplinks: 4, // 4x100GbE QSFP28 uplinks
  },
  power: {
    typicalWatts: 100,
    maxWatts: 150,
    idleWatts: 50,
    perPortWatts: 1,
    psuConfig: {
      count: 1,
      capacity: 200,
      redundancy: 'N',
    },
  },
  physical: {
    rackUnits: 1,
    widthMm: 438,
    depthMm: 300,
    weightKg: 5,
    airflow: 'front-to-back',
    maxBtuPerHour: 512,
  },
  performance: {
    switchingCapacityTbps: 0.448, // 48x1G + 4x100G
    forwardingRateMpps: 333,
    latencyNs: 1000, // 1GbE typical
    bufferSizeMB: 16,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: false,
    evpn: false,
    rdma: false,
    adaptiveRouting: false,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    sonic: true,
    additionalFeatures: [
      'NVIDIA Spectrum ASIC',
      'Cumulus Linux compatible',
      'Non-blocking 100GbE uplinks',
      'Optimized for OOB management',
    ],
  },
  metadata: {
    productUrl: 'https://docs.nvidia.com/networking/display/sn2201switchesum/introduction',
    datasheet: 'https://www.delltechnologies.com/asset/en-us/products/networking/technical-support/nvidia-spectrum-sn2201-datasheet.pdf',
    releaseYear: 2018,
    endOfLife: false,
    notes: 'Out-of-band management switch. 48 x 1GbE RJ45 access ports + 4 x 100GbE QSFP28 uplinks.',
  },
};

/**
 * All NVIDIA Ethernet switches
 */
export const NVIDIA_ETHERNET_SWITCHES: readonly SwitchModel[] = [
  SPECTRUM_SN5600,
  SPECTRUM_SN4600,
  SPECTRUM_SN4600C,
  SPECTRUM_SN3800,
  SPECTRUM_SN2201,
] as const;

/**
 * Get NVIDIA Ethernet switch by ID
 */
export function getNvidiaEthernetSwitch(id: string): SwitchModel | undefined {
  return NVIDIA_ETHERNET_SWITCHES.find((sw) => sw.id === id);
}

/**
 * Get NVIDIA Ethernet switches by speed
 */
export function getNvidiaEthernetSwitchesBySpeed(speedGbps: number): readonly SwitchModel[] {
  return NVIDIA_ETHERNET_SWITCHES.filter((sw) => sw.ports.speed.value === speedGbps);
}

/**
 * Get recommended NVIDIA Ethernet switch for role and scale
 */
export function getRecommendedNvidiaEthernetSwitch(
  role: 'leaf' | 'spine' | 'management',
  speedGbps: number = 400
): SwitchModel {
  if (role === 'management') {
    return SPECTRUM_SN2201;
  }

  // Select based on speed requirement
  if (speedGbps >= 800) {
    return SPECTRUM_SN5600;
  }

  if (speedGbps >= 200) {
    return SPECTRUM_SN4600;
  }

  if (speedGbps >= 100) {
    return role === 'spine' ? SPECTRUM_SN3800 : SPECTRUM_SN4600C;
  }

  return SPECTRUM_SN4600C;
}
